import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Flame,
  Footprints,
  MapPin,
  MessageCircle,
  Moon,
  TrendingUp,
  Users,
} from "lucide-react";
import { Card } from "./MobileShell";
import { clientLogs, clientListSessions } from "../lib/api";
import { PILLARS } from "./data/pillars";

export default function HomeScreen() {
  const { profile, token } = useOutletContext();
  const [logs, setLogs] = useState({ daily: [], weekly: [], monthly: [] });
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [l, s] = await Promise.all([clientLogs(token, 14), clientListSessions(token)]);
        if (!active) return;
        setLogs({ daily: l.daily || [], weekly: l.weekly || [], monthly: l.monthly || [] });
        setSessions(s || []);
      } catch {
        /* ignore */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const todayISO = new Date().toISOString().slice(0, 10);
  const today = useMemo(() => logs.daily.find((d) => d.log_date === todayISO), [logs, todayISO]);
  const upcoming = useMemo(
    () => sessions.filter((s) => s.is_rsvped).slice(0, 1)[0] || sessions[0] || null,
    [sessions]
  );
  const firstName = (profile?.name || "").split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <>
      <header className="mb-5" data-testid="home-header">
        <div className="eyebrow text-[var(--lc-muted)]">{greeting}</div>
        <h1 className="font-serif-display text-[30px] leading-tight mt-1">
          {firstName}, <span className="italic">today's circle.</span>
        </h1>
      </header>

      {/* Plan badge */}
      <Card className="mb-4" data-testid="home-plan-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow text-[var(--lc-moss)]">Current plan</div>
            <div className="font-serif-display text-xl mt-1">
              {profile?.package_name || "Performance"}
            </div>
            <p className="text-xs text-[var(--lc-muted)] mt-1">
              Your plan is tailored by Coach Arvind — ask him to tune it any time.
            </p>
          </div>
          <Link
            to="/packages"
            className="text-[11px] eyebrow text-[var(--lc-ink)] inline-flex items-center gap-1"
          >
            Packages <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </Card>

      {/* Today quick stats */}
      <Card className="mb-4" data-testid="home-today-card">
        <div className="eyebrow text-[var(--lc-muted)] mb-3">Today</div>
        <div className="grid grid-cols-3 gap-3">
          <QuickStat
            icon={Flame}
            label="Meals"
            value={today?.food?.length ? `${today.food.length} logged` : "—"}
          />
          <QuickStat
            icon={Footprints}
            label="Steps"
            value={today?.steps ? today.steps.toLocaleString() : "—"}
          />
          <QuickStat
            icon={Moon}
            label="Sleep"
            value={today?.sleep_h ? `${today.sleep_h} h` : "—"}
          />
        </div>
        <Link
          to="/portal"
          className="mt-3 inline-flex items-center gap-1 text-[11px] eyebrow text-[var(--lc-moss-dark)] hover:text-[var(--lc-ink)]"
          data-testid="home-open-tracker"
        >
          Open daily tracker <ChevronRight className="h-3 w-3" />
        </Link>
      </Card>

      {/* Upcoming session */}
      <Card className="mb-4" data-testid="home-session-card">
        <div className="eyebrow text-[var(--lc-muted)] mb-2">Upcoming session</div>
        {loading ? (
          <div className="h-14 bg-[var(--lc-cream)] rounded animate-pulse" />
        ) : upcoming ? (
          <>
            <div className="font-serif-display text-xl leading-tight">{upcoming.title}</div>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--lc-muted)]">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" />
                {new Date(upcoming.start_at).toLocaleString("en-GB", {
                  weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                })}
              </span>
              {upcoming.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" /> {upcoming.location}
                </span>
              )}
              {upcoming.type === "group" && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3 w-3" /> {upcoming.confirmed_count}/{upcoming.capacity}
                </span>
              )}
            </div>
            {!upcoming.is_rsvped && (
              <Link
                to="/m/coach"
                className="mt-3 inline-block px-3 py-1.5 bg-[var(--lc-ink)] text-white text-[10px] font-bold tracking-[0.14em] uppercase rounded-md"
                data-testid="home-rsvp-link"
              >
                Review & RSVP
              </Link>
            )}
          </>
        ) : (
          <div className="text-sm text-[var(--lc-muted)]">
            No sessions scheduled. Message Coach Arvind to book one.
          </div>
        )}
      </Card>

      {/* Quick progress */}
      <Card className="mb-4" data-testid="home-progress-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow text-[var(--lc-muted)]">Progress snapshot</div>
            <div className="font-serif-display text-xl mt-1 flex items-center gap-2">
              {logs.daily.length}
              <span className="text-sm text-[var(--lc-muted)] font-sans">
                day{logs.daily.length === 1 ? "" : "s"} logged · 14-day window
              </span>
            </div>
          </div>
          <TrendingUp className="h-5 w-5 text-[var(--lc-moss-dark)]" />
        </div>
        <Link
          to="/m/progress"
          className="mt-3 inline-flex items-center gap-1 text-[11px] eyebrow text-[var(--lc-moss-dark)] hover:text-[var(--lc-ink)]"
          data-testid="home-open-progress"
        >
          Full progress <ChevronRight className="h-3 w-3" />
        </Link>
      </Card>

      {/* Contact coach quick */}
      <Link
        to="/m/coach"
        className="mb-5 flex items-center justify-between gap-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] rounded-xl px-4 py-4"
        data-testid="home-contact-coach"
      >
        <div>
          <div className="eyebrow text-[var(--lc-cream)]/60">Need a nudge?</div>
          <div className="font-serif-display text-lg mt-0.5">Message Coach Arvind</div>
        </div>
        <MessageCircle className="h-5 w-5" />
      </Link>

      {/* Six pillars overview */}
      <div className="mb-2 eyebrow text-[var(--lc-muted)]">The Six Pillars</div>
      <div className="grid grid-cols-2 gap-3 mb-6" data-testid="home-pillars">
        {PILLARS.map((p) => (
          <Link
            key={p.id}
            to={`/m/plan#${p.id}`}
            className="bg-white border border-[var(--lc-line)] rounded-xl p-3.5 active:scale-[0.98] transition-transform"
            data-testid={`home-pillar-${p.id}`}
          >
            <div className={`w-9 h-9 rounded-md border grid place-items-center ${p.tint}`}>
              <p.icon className="h-4 w-4" />
            </div>
            <div className="mt-2.5 font-semibold text-sm leading-tight">{p.name}</div>
            <div className="text-[10px] text-[var(--lc-muted)] mt-1 tracking-[0.14em] uppercase">
              {p.activities.length} activities
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

function QuickStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-md p-2.5 text-center">
      <Icon className="h-4 w-4 mx-auto text-[var(--lc-moss-dark)]" />
      <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--lc-muted)] mt-1.5">{label}</div>
      <div className="font-serif-display text-base leading-tight mt-0.5">{value}</div>
    </div>
  );
}
